import { useMemo } from "react";
import { z } from "zod";
import type { useChangeSet, useInvestorInfo } from "creso-core/hooks";
import type { InvestmentAccount } from "creso-core/types";
import { DEFAULT_DOB_DATE } from "creso-core/constants";
import { AccountCenterFormSchema, AccountCenterFormValues } from "./form-schema";

type InvestorInfo = ReturnType<typeof useInvestorInfo>["data"];
type ChangeSet = ReturnType<typeof useChangeSet>[0];

/**
 * Seeds the account form from saved investor data, with any pending change-set
 * value winning over the saved one — so a client reopening the page sees the
 * edits awaiting authorisation rather than the old values.
 */
export function useAccountFormValues(
  data: InvestorInfo,
  changeSet: ChangeSet
): AccountCenterFormValues {
  const defaultBankAccountsValue = useMemo(() => {
    const bankAccounts: AccountCenterFormValues["bank"] = [];

    const savedBankAccounts =
      data.bank?.toSorted((a, b) => {
        try {
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        } catch {
          return 0;
        }
      }) || [];

    savedBankAccounts.forEach((acc) => {
      const accountChangeSet =
        changeSet?.INVESTOR_BANK_ACCOUNT?.find(
          (c) => c.bank_account_id === acc.id
        ) || {};

      bankAccounts.push({
        account_number: (accountChangeSet.account_number ||
          acc.account_number ||
          "") as string,
        ifsc_code: (accountChangeSet.ifsc_code ||
          acc.ifsc_code ||
          "") as string,
        type: (accountChangeSet.type || acc.type || "") as string,
        bank_account_id: (accountChangeSet.bank_account_id || acc.id) as number,
        is_default:
          (accountChangeSet.is_default as boolean) || acc.is_default || false,
        primary_account_holder_name:
          (accountChangeSet.primary_account_holder_name as string) ||
          acc.primary_account_holder_name ||
          "",
      });
    });

    const newBankAccounts = changeSet?.INVESTOR_BANK_ACCOUNT?.filter(
      (c) => !c.bank_account_id
    ).reduce(
      (acc, curr) => {
        acc.push({
          account_number: (curr.account_number || "") as string,
          ifsc_code: (curr.ifsc_code || "") as string,
          type: (curr.type || "") as string,
          primary_account_holder_name: (curr.primary_account_holder_name ||
            "") as string,
        });
        return acc;
      },
      [] as z.infer<typeof AccountCenterFormSchema>["bank"]
    );

    if (newBankAccounts?.length) bankAccounts.push(...newBankAccounts);
    return bankAccounts as z.infer<typeof AccountCenterFormSchema>["bank"];
  }, [data.bank, changeSet]);

  const defaultNomineesValue = useMemo(() => {
    const nominees: AccountCenterFormValues["nominees"] = [];

    // data.nominee is an array of nominees
    const savedNominees = data.nominee || [];

    savedNominees.forEach((nom) => {
      const nomineeChangeSet =
        changeSet?.NOMINEE?.find(
          (c: Record<string, unknown>) => c.nominee_id === nom.id
        ) || ({} as Record<string, unknown>);

      nominees.push({
        nominee_id: (nomineeChangeSet.nominee_id || nom.id) as string,
        name: (nomineeChangeSet.name || nom.name || "") as string,
        relationship: (nomineeChangeSet.relationship ||
          nom.relationship ||
          "") as string,
        identity_type: (nomineeChangeSet.identity_type ||
          nom.identity_type ||
          "") as string,
        identity_number: (nomineeChangeSet.identity_number ||
          nom.identity_number ||
          "") as string,
        email: (nomineeChangeSet.email || nom.email || "") as string,
        mobile: (nomineeChangeSet.mobile || nom.mobile || "") as string,
        date_of_birth: (nomineeChangeSet.date_of_birth ||
          nom.date_of_birth ||
          null) as string | null,
        guardian_name: (nomineeChangeSet.guardian_name ||
          nom.guardian_name ||
          "") as string,
        guardian_pan: (nomineeChangeSet.guardian_pan ||
          nom.guardian_pan ||
          "") as string,
        address_line_1: (nomineeChangeSet.address_line_1 ||
          nom.address_line_1 ||
          "") as string,
        address_line_2: (nomineeChangeSet.address_line_2 ||
          nom.address_line_2 ||
          "") as string,
        address_line_3: (nomineeChangeSet.address_line_3 ||
          nom.address_line_3 ||
          "") as string,
        country: (nomineeChangeSet.country || nom.country || "IN") as string,
        city: (nomineeChangeSet.city || nom.city || "") as string,
        pin_code: (nomineeChangeSet.pin_code || nom.pin_code || "") as string,
        applicable_percentage: (nomineeChangeSet.applicable_percentage ||
          nom.applicable_percentage ||
          100) as number,
      });
    });

    const newNominees = changeSet?.NOMINEE?.filter(
      (c: Record<string, unknown>) => !c.nominee_id
    ).reduce(
      (
        acc: z.infer<typeof AccountCenterFormSchema>["nominees"],
        curr: Record<string, unknown>
      ) => {
        acc.push({
          name: (curr.name || "") as string,
          relationship: (curr.relationship || "") as string,
          identity_type: (curr.identity_type || "") as string,
          identity_number: (curr.identity_number || "") as string,
          email: (curr.email || "") as string,
          mobile: (curr.mobile || "") as string,
          date_of_birth: (curr.date_of_birth || null) as string | null,
          guardian_name: (curr.guardian_name || "") as string,
          guardian_pan: (curr.guardian_pan || "") as string,
          address_line_1: (curr.address_line_1 || "") as string,
          address_line_2: (curr.address_line_2 || "") as string,
          address_line_3: (curr.address_line_3 || "") as string,
          country: (curr.country || "IN") as string,
          city: (curr.city || "") as string,
          pin_code: (curr.pin_code || "") as string,
          applicable_percentage: (curr.applicable_percentage || 100) as number,
        });
        return acc;
      },
      [] as z.infer<typeof AccountCenterFormSchema>["nominees"]
    );

    if (newNominees?.length) nominees.push(...newNominees);
    return nominees as z.infer<typeof AccountCenterFormSchema>["nominees"];
  }, [data.nominee, changeSet]);

  return useMemo(
    () => ({
      profile: {
        name: data.profile?.name || "",
        pan: data.profile?.pan || "",
        gender: (changeSet?.INVESTOR_PROFILE?.gender ||
          data.profile?.gender ||
          "") as string,
        date_of_birth: (changeSet?.INVESTOR_PROFILE?.date_of_birth ||
          data.profile?.date_of_birth ||
          DEFAULT_DOB_DATE) as string,
        country_of_birth: (changeSet?.INVESTOR_PROFILE?.country_of_birth ||
          data.profile?.country_of_birth ||
          "IN") as string,
      },
      address: {
        line1: (changeSet.INVESTOR_ADDRESS_INFORMATION?.line1 ||
          data.address?.line1 ||
          "") as string,
        line2: (changeSet.INVESTOR_ADDRESS_INFORMATION?.line2 ||
          data.address?.line2 ||
          "") as string,
        line3: (changeSet.INVESTOR_ADDRESS_INFORMATION?.line3 ||
          data.address?.line3 ||
          "") as string,
        country: (changeSet.INVESTOR_ADDRESS_INFORMATION?.country ||
          data.address?.country ||
          "IN") as string,
        city: (changeSet.INVESTOR_ADDRESS_INFORMATION?.city ||
          data.address?.city ||
          "") as string,
        state: (changeSet.INVESTOR_ADDRESS_INFORMATION?.state ||
          data.address?.state ||
          "") as string,
        postal_code: (changeSet.INVESTOR_ADDRESS_INFORMATION?.postal_code ||
          data.address?.postal_code ||
          "") as string,
      },
      bank: defaultBankAccountsValue,
      nominees: defaultNomineesValue,
      contact: {
        number: data.contact?.number || "",
        phone_belongs_to: (changeSet.INVESTOR_CONTACT_INFORMATION
          ?.phone_belongs_to ||
          data.contact?.phone_belongs_to ||
          "self") as string,
        email: data.contact?.email || "",
        email_belongs_to: (changeSet.INVESTOR_CONTACT_INFORMATION
          ?.email_belongs_to ||
          data.contact?.email_belongs_to ||
          "self") as string,
        isd: "+91",
      },
      account: {
        ...(data.account as InvestmentAccount),
        nomination_opt: (changeSet.INVESTMENT_ACCOUNT?.nomination_opt ||
          data.account?.nomination_opt ||
          "") as "Y" | "N" | "NA",
      },
      other: {
        occupation: (changeSet.INVESTOR_PROFILE?.OCCUPATION ||
          data.profile?.occupation ||
          "") as string,
        income_slab: (changeSet.INVESTOR_PROFILE?.INCOME_SLAB ||
          data.profile?.income_slab ||
          "") as string,
        source_of_wealth: (changeSet.INVESTOR_PROFILE?.SOURCE_OF_WEALTH ||
          data.profile?.source_of_wealth ||
          "") as string,
        pep_details: (changeSet.INVESTOR_PROFILE?.PEP_DETAILS ||
          data.profile?.pep_details ||
          "") as string,
      },
    }),
    [data, changeSet, defaultBankAccountsValue, defaultNomineesValue]
  );
}
